import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { FuaAssessmentQuestionnaireItem } from 'components/Assessments/FuaAssessment/types/questionnaire'
import { AuthorizationFormField } from './formField'

/**
 * Authorization subsection adapter for FUA Assessment
 * Handles authorization extraction and transformation
 */
export class AuthorizationAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract authorization source (single value for FUA)
    const authorizationSource = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.AUTHORIZATION_AUTHORIZATION_AUTHORIZATION_SOURCE
    ).at(0)

    const currentHours = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.AUTHORIZATION_AUTHORIZATION_CURRENT_HOURS
    ).at(0)

    const acutePeriodCurrentActive = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.AUTHORIZATION_ACUTE_PERIOD_CURRENT_ACTIVE
    ).at(0)

    // Extract date strings (stored as UTC in FHIR)
    const acutePeriodStartUTC = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.AUTHORIZATION_ACUTE_PERIOD_PERIOD_START
    ).at(0)

    const acutePeriodEndUTC = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.AUTHORIZATION_ACUTE_PERIOD_PERIOD_END
    ).at(0)

    // Convert UTC date strings to caregiver timezone for form display
    const acutePeriodStartDayjs = acutePeriodStartUTC
      ? options?.caregiverTimezone
        ? dayjs.utc(acutePeriodStartUTC).tz(options.caregiverTimezone).startOf('day')
        : dayjs.utc(acutePeriodStartUTC).startOf('day')
      : undefined

    const acutePeriodEndDayjs = acutePeriodEndUTC
      ? options?.caregiverTimezone
        ? dayjs.utc(acutePeriodEndUTC).tz(options.caregiverTimezone).startOf('day')
        : dayjs.utc(acutePeriodEndUTC).startOf('day')
      : undefined

    const acutePeriodPeriod: [Dayjs | undefined, Dayjs | undefined] | undefined =
      acutePeriodStartDayjs && acutePeriodEndDayjs
        ? [acutePeriodStartDayjs, acutePeriodEndDayjs]
        : undefined

    return {
      [AuthorizationFormField.AUTHORIZATION_AUTHORIZATION_AUTHORIZATION_SOURCE]: Array.isArray(
        authorizationSource
      )
        ? authorizationSource?.map((source) => source.reference)
        : authorizationSource?.reference,
      [AuthorizationFormField.AUTHORIZATION_AUTHORIZATION_CURRENT_HOURS]: currentHours,
      [AuthorizationFormField.AUTHORIZATION_ACUTE_PERIOD_CURRENT_ACTIVE]: acutePeriodCurrentActive
        ? { reference: acutePeriodCurrentActive.reference }
        : undefined,
      [AuthorizationFormField.AUTHORIZATION_ACUTE_PERIOD_PERIOD]: acutePeriodPeriod
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Build authorization source and current hours mutations
    if (
      this.formDataFieldExists(
        AuthorizationFormField.AUTHORIZATION_AUTHORIZATION_AUTHORIZATION_SOURCE
      )
    ) {
      const authorizationSource = this.getFormDataField(
        AuthorizationFormField.AUTHORIZATION_AUTHORIZATION_AUTHORIZATION_SOURCE
      )

      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.AUTHORIZATION_AUTHORIZATION_AUTHORIZATION_SOURCE,
        value: Array.isArray(authorizationSource)
          ? authorizationSource?.map((source) => (source ? { reference: source } : undefined))
          : authorizationSource
          ? { reference: authorizationSource }
          : undefined
      })

      // Current hours is always set when authorization source exists
      const currentHours = this.getFormDataField(
        AuthorizationFormField.AUTHORIZATION_AUTHORIZATION_CURRENT_HOURS
      )

      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.AUTHORIZATION_AUTHORIZATION_CURRENT_HOURS,
        value: currentHours
      })
    }

    // Build acute period current active mutation
    if (
      this.formDataFieldExists(AuthorizationFormField.AUTHORIZATION_ACUTE_PERIOD_CURRENT_ACTIVE)
    ) {
      const acutePeriodCurrentActive = this.getFormDataField(
        AuthorizationFormField.AUTHORIZATION_ACUTE_PERIOD_CURRENT_ACTIVE
      )

      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.AUTHORIZATION_ACUTE_PERIOD_CURRENT_ACTIVE,
        value: acutePeriodCurrentActive
      })
    }

    // Build acute period date range mutations
    if (this.formDataFieldExists(AuthorizationFormField.AUTHORIZATION_ACUTE_PERIOD_PERIOD)) {
      const acutePeriodPeriod = this.getFormDataField(
        AuthorizationFormField.AUTHORIZATION_ACUTE_PERIOD_PERIOD
      )

      if (acutePeriodPeriod?.[0] && acutePeriodPeriod?.[1]) {
        // Convert to UTC before formatting to ensure FHIR DATE fields (which are interpreted as UTC)
        mutations.push(
          {
            linkId: FuaAssessmentQuestionnaireItem.AUTHORIZATION_ACUTE_PERIOD_PERIOD_START,
            value: acutePeriodPeriod[0].utc().startOf('day').format('YYYY-MM-DD')
          },
          {
            linkId: FuaAssessmentQuestionnaireItem.AUTHORIZATION_ACUTE_PERIOD_PERIOD_END,
            value: acutePeriodPeriod[1].utc().startOf('day').format('YYYY-MM-DD')
          }
        )
      }
    }

    return mutations
  }
}
